import * as React from 'react'
import { Form } from 'react-final-form'

import { useContent } from '@ngb-frontend/shared/context'
import { ContactType } from '@ngb-frontend/shared/types'
import {
  DRIVER_STEP_FORM_ID,
  OtherContactSection,
  StepFooterDisabledSync,
} from '@ngb-frontend/shared/ui'
import { string } from '@ngb-frontend/shared/utils'

import {
  ExpandingRadioCard,
  ExpandingRadioCardGroup,
} from '../../ExpandingRadioCard/ExpandingRadioCard'

import type { ContactFormValues } from '@ngb-frontend/shared/types'

export interface ContactFormProps {
  groupLabel: string
  driverFormValues?: ContactFormValues
  contactType?: ContactType
  formValues?: ContactFormValues
  disableDriver?: boolean
  onContactChange?: (type: ContactType) => void
  onSubmit?: (values: ContactFormValues, contactType: ContactType) => void
}

export const ContactForm: React.FC<ContactFormProps> = ({
  groupLabel,
  driverFormValues,
  contactType,
  formValues,
  disableDriver,
  onContactChange,
  onSubmit,
}) => {
  const c = useContent()
  const otherSelected = !!(contactType === ContactType.Other || disableDriver)

  const handleOtherSubmit = React.useCallback(
    (values: ContactFormValues) => onSubmit?.(values, ContactType.Other),
    [onSubmit],
  )

  return (
    <Form<ContactFormValues>
      onSubmit={handleOtherSubmit}
      initialValues={formValues}
    >
      {(formProps) => (
        <form
          id={DRIVER_STEP_FORM_ID}
          data-e2e-component="contact-form"
          onSubmit={(event) => {
            if (otherSelected) {
              formProps.handleSubmit(event)
              return
            }

            event.preventDefault()
            if (driverFormValues)
              onSubmit?.(driverFormValues, ContactType.Driver)
          }}
        >
          <ExpandingRadioCardGroup label={groupLabel}>
            {driverFormValues && (
              <ExpandingRadioCard
                testId="driver-radio-card"
                actionName="User contact"
                name="contactType"
                value={ContactType.Driver}
                title={string([
                  driverFormValues.firstName,
                  driverFormValues.lastName,
                ])}
                details={
                  <>
                    <span>{driverFormValues.email}</span>
                    <br />
                    <span>{driverFormValues.phone}</span>
                  </>
                }
                disabled={disableDriver}
                checked={!otherSelected}
                onChange={() => onContactChange?.(ContactType.Driver)}
              />
            )}
            <ExpandingRadioCard
              testId="other-radio-card"
              name="contactType"
              value={ContactType.Other}
              title={c('fields.contact.otherContactTitle')}
              details={c('fields.contact.otherContactDescription')}
              checked={otherSelected}
              onChange={() => onContactChange?.(ContactType.Other)}
            >
              <OtherContactSection compact hideTitle />
            </ExpandingRadioCard>
          </ExpandingRadioCardGroup>
          <StepFooterDisabledSync
            disabled={
              contactType === undefined || (otherSelected && formProps.invalid)
            }
          />
        </form>
      )}
    </Form>
  )
}
